import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import styles from './TutorChat.module.css';
import { copyText } from './clipboard';
import { balanceStreamingMarkdown } from './balanceMarkdown';
import { cleanCitationTags } from './citationTags';
import type { TutorSource } from './types';

/**
 * Safe markdown for tutor replies.
 *
 * - Raw HTML in the source is NOT rendered (react-markdown drops it; we do not
 *   add rehype-raw), so model output cannot inject markup.
 * - Links are limited to http(s)/mailto and always open in a new tab with
 *   noopener noreferrer. Anything else (javascript:, data:, ...) loses its href.
 * - Images are never fetched (tracking / mixed-content); alt text is shown.
 */
const ALLOWED_URL = /^(https?:|mailto:)/i;
export const safeUrl = (url: string): string => (ALLOWED_URL.test(url.trim()) ? url : '');

function textOf(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

export const CopyButton: React.FC<{
  text: string;
  label: string;
  className?: string;
}> = ({ text, label, className }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const onClick = async () => {
    if (await copyText(text)) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <>
    <button type="button" className={className ?? styles.codeCopy} onClick={onClick} aria-label={label}>
      {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
      <span className={styles.btnLabel}>{copied ? t("tutor.copied") : t("tutor.copy")}</span>
    </button>
    <span className={styles.srOnly} role="status">{copied ? t('tutor.copied') : ''}</span>
    </>
  );
};

const CodeBlock: React.FC<{ language?: string; code: string }> = ({ language, code }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        <span className={styles.codeLang}>{language || t('tutor.code')}</span>
        <CopyButton text={code} label={t('tutor.copy_code')} />
      </div>
      <pre className={styles.codePre} tabIndex={0} aria-label={t('tutor.code_region')}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const TutorMarkdown: React.FC<{ content: string; streaming?: boolean; sources?: TutorSource[] }> = ({ content, streaming, sources }) => (
  <div className={styles.markdown}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      skipHtml
      urlTransform={safeUrl}
      components={{
        pre: ({ children }) => {
          const child = React.Children.toArray(children)[0];
          const props = (React.isValidElement(child) ? child.props : {}) as {
            className?: string;
            children?: React.ReactNode;
          };
          const lang = /language-([\w+#-]+)/.exec(props.className || '')?.[1];
          return <CodeBlock language={lang} code={textOf(props.children).replace(/\n$/, '')} />;
        },
        code: ({ className, children }) => (
          <code className={className ?? styles.inlineCode}>{children}</code>
        ),
        a: ({ href, children }) =>
          href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
              {children}
            </a>
          ) : (
            <span>{children}</span>
          ),
        img: ({ alt }) => (alt ? <span>{alt}</span> : null),
        table: ({ children }) => (
          <div className={styles.tableWrap} tabIndex={0}>
            <table className={styles.table}>{children}</table>
          </div>
        ),
      }}
    >
      {(() => {
        const clean = cleanCitationTags(content, { sources, streaming });
        return streaming ? balanceStreamingMarkdown(clean) : clean;
      })()}
    </ReactMarkdown>
  </div>
);

export default TutorMarkdown;
